import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RoundProductOverride,
  DOSE_UNITS,
  DOSE_DENOMINATORS,
  PHI_V2_UNITS,
  DoseUnit,
  DoseDenominator,
  PhiV2Unit,
  Dose,
  PhiV2,
} from '../../services/sprayService';
import type { PestRef } from '../../services/pestService';
import { Field } from '../patterns';
import PestMultiSelect from './PestMultiSelect';

interface Props {
  value: RoundProductOverride;
  onChange: (next: RoundProductOverride) => void;
  pestOptions?: PestRef[];
}

export const RoundProductOverrideEditor: React.FC<Props> = ({ value, onChange, pestOptions = [] }) => {
  const { t } = useTranslation();

  const [doseEnabled, setDoseEnabled] = useState(Boolean(value.doseOverride));
  const [phiEnabled,  setPhiEnabled]  = useState(Boolean(value.phiOverride));

  const [doseDraft, setDoseDraft] = useState<Dose>(
    value.doseOverride ?? { value: 0, unit: '' as DoseUnit, denominator: '100L' as DoseDenominator }
  );
  const [phiDraft, setPhiDraft] = useState<PhiV2>(
    value.phiOverride ?? { value: 0, unit: 'days' as PhiV2Unit }
  );

  useEffect(() => {
    setDoseEnabled(Boolean(value.doseOverride));
    if (value.doseOverride) setDoseDraft(value.doseOverride);
  }, [value.doseOverride]);

  useEffect(() => {
    setPhiEnabled(Boolean(value.phiOverride));
    if (value.phiOverride) setPhiDraft(value.phiOverride);
  }, [value.phiOverride]);

  const update = (patch: Partial<RoundProductOverride>) => onChange({ ...value, ...patch });

  const updateDose = (patch: Partial<Dose>) => {
    const next = { ...doseDraft, ...patch };
    setDoseDraft(next);
    update({ doseOverride: next });
  };

  const updatePhi = (patch: Partial<PhiV2>) => {
    const next = { ...phiDraft, ...patch };
    setPhiDraft(next);
    update({ phiOverride: next });
  };

  const toggleDose = () => {
    if (doseEnabled) {
      setDoseEnabled(false);
      update({ doseOverride: undefined });
    } else {
      setDoseEnabled(true);
    }
  };

  const togglePhi = () => {
    if (phiEnabled) {
      setPhiEnabled(false);
      update({ phiOverride: undefined });
    } else {
      setPhiEnabled(true);
    }
  };

  return (
    <div data-testid="round-product-override-editor">
      <div className="mb-2">
        <Field
          label={t('spray.programs.roundProducts.targetPestsLabel', { defaultValue: 'Target pests' })}
          controlId="round-product-override-target-pests"
        >
          <PestMultiSelect
            options={pestOptions}
            value={value.targetPests || []}
            onChange={targetPests => update({ targetPests })}
          />
        </Field>
      </div>

      <div className="d-flex gap-2 align-items-center mb-2">
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          data-testid="round-product-override-dose-toggle"
          onClick={toggleDose}
        >
          {doseEnabled ? '✓ ' : ''}{t('spray.programs.roundProducts.overrideDoseToggle', { defaultValue: 'Use a custom dose' })}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          data-testid="round-product-override-phi-toggle"
          onClick={togglePhi}
        >
          {phiEnabled ? '✓ ' : ''}{t('spray.programs.roundProducts.overridePhiToggle', { defaultValue: 'Use a custom PHI' })}
        </button>
      </div>

      {doseEnabled && (
        <div className="pat-field-grid pat-field-grid--3 mb-2">
          <Field
            label={t('spray.programs.roundProducts.doseValue', { defaultValue: 'Dose value' })}
            controlId="round-product-override-dose-value"
          >
            <input
              id="round-product-override-dose-value"
              data-testid="round-product-override-dose-value"
              type="number"
              className="pat-input"
              value={doseDraft.value || ''}
              onChange={e => updateDose({ value: Number(e.target.value) || 0 })}
            />
          </Field>
          <Field
            label={t('spray.programs.roundProducts.doseUnit', { defaultValue: 'Unit' })}
            controlId="round-product-override-dose-unit"
          >
            <select
              id="round-product-override-dose-unit"
              data-testid="round-product-override-dose-unit"
              className="pat-input"
              value={doseDraft.unit || ''}
              onChange={e => updateDose({ unit: e.target.value as DoseUnit })}
            >
              <option value="">—</option>
              {DOSE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field
            label={t('spray.programs.roundProducts.doseDenominator', { defaultValue: 'Per' })}
            controlId="round-product-override-dose-denominator"
          >
            <select
              id="round-product-override-dose-denominator"
              data-testid="round-product-override-dose-denominator"
              className="pat-input"
              value={doseDraft.denominator || ''}
              onChange={e => updateDose({ denominator: e.target.value as DoseDenominator })}
            >
              <option value="">—</option>
              {DOSE_DENOMINATORS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </div>
      )}

      {phiEnabled && (
        <div className="pat-field-grid pat-field-grid--2 mb-2">
          <Field
            label={t('spray.programs.roundProducts.phiValue', { defaultValue: 'PHI value' })}
            controlId="round-product-override-phi-value"
          >
            <input
              id="round-product-override-phi-value"
              data-testid="round-product-override-phi-value"
              type="number"
              className="pat-input"
              value={phiDraft.value || ''}
              onChange={e => updatePhi({ value: Number(e.target.value) || 0 })}
            />
          </Field>
          <Field
            label={t('spray.programs.roundProducts.phiUnit', { defaultValue: 'PHI unit' })}
            controlId="round-product-override-phi-unit"
          >
            <select
              id="round-product-override-phi-unit"
              data-testid="round-product-override-phi-unit"
              className="pat-input"
              value={phiDraft.unit || ''}
              onChange={e => updatePhi({ unit: e.target.value as PhiV2Unit })}
            >
              <option value="">—</option>
              {PHI_V2_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>
      )}

      <div className="text-muted">
        {t('spray.programs.roundProducts.fromAgronomist', {
          defaultValue: "Use this when the agronomist's program prescribes specific values that differ from the label.",
        })}
      </div>
    </div>
  );
};

export default RoundProductOverrideEditor;
